"use client";
import { X } from "lucide-react";

import React from "react";
import { ChevronDown, ChevronRight, Loader2, Search } from "lucide-react";
import { CustomModal } from "./CustomModal";

type ContentCategoryNode = {
  id: string;
  name_en: string;
  name_fa: string;
  path?: string;
  children?: ContentCategoryNode[];
};

type ContentItem = {
  id: string;
  slug_en?: string | null;
  slug_fa?: string | null;
  slug_en_published?: string | null;
  slug_fa_published?: string | null;
  title_en?: string | null;
  title_fa?: string | null;
  title_en_published?: string | null;
  title_fa_published?: string | null;
  tags?: Array<{ id: string; name_en: string; name_fa: string }>;
  contentCategories?: Array<{
    contentCategory: { id: string; name_en: string; name_fa: string };
  }>;
};

function useIsSmallScreen() {
  const [isSmall, setIsSmall] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia("(max-width: 767px)");
    const handleChange = (event: MediaQueryListEvent) =>
      setIsSmall(event.matches);

    setIsSmall(mq.matches);

    if ("addEventListener" in mq) {
      mq.addEventListener("change", handleChange);
      return () => mq.removeEventListener("change", handleChange);
    }

    const legacyMq = mq as MediaQueryList & {
      addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
      removeListener?: (listener: (event: MediaQueryListEvent) => void) => void;
    };
    legacyMq.addListener?.(handleChange);
    return () => {
      legacyMq.removeListener?.(handleChange);
    };
  }, []);

  return isSmall;
}

export function ContentLinkPicker({
  open,
  onClose,
  onSelect,
  initialSlug,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (link: { contentSlug: string; contentId?: string }) => void;
  initialSlug?: string;
}) {
  const isSmall = useIsSmallScreen();
  const [search, setSearch] = React.useState(initialSlug ?? "");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [tagFilter, setTagFilter] = React.useState("");
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<
    string | null
  >(null);
  const [collapsedNodes, setCollapsedNodes] = React.useState<
    Record<string, boolean>
  >({});

  const [treeLoading, setTreeLoading] = React.useState(false);
  const [treeError, setTreeError] = React.useState<string | null>(null);
  const [categoryTree, setCategoryTree] = React.useState<ContentCategoryNode[]>(
    [],
  );

  const [tagsLoading, setTagsLoading] = React.useState(false);
  const [tagsError, setTagsError] = React.useState<string | null>(null);
  const [tags, setTags] = React.useState<
    Array<{ id: string; name_en: string; name_fa: string }>
  >([]);

  const [contentLoading, setContentLoading] = React.useState(false);
  const [contentError, setContentError] = React.useState<string | null>(null);
  const [contentItems, setContentItems] = React.useState<ContentItem[]>([]);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTreeLoading(true);
    setTreeError(null);
    fetch("/api/content-categories")
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok)
          throw new Error(data?.error || "Failed to load content tree");
        if (!cancelled) setCategoryTree(Array.isArray(data) ? data : []);
      })
      .catch((err: any) => {
        if (!cancelled)
          setTreeError(err?.message || "Failed to load content tree");
      })
      .finally(() => {
        if (!cancelled) setTreeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTagsLoading(true);
    setTagsError(null);
    fetch("/api/cms/tags?take=200&page=1")
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to load tags");
        if (!cancelled) setTags(Array.isArray(data?.items) ? data.items : []);
      })
      .catch((err: any) => {
        if (!cancelled)
          setTagsError(err?.message || "Failed to load tags");
      })
      .finally(() => {
        if (!cancelled) setTagsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const params = new URLSearchParams();
    params.set("take", "200");
    if (debouncedSearch) params.set("q", debouncedSearch);
    if (tagFilter) params.set("tagId", tagFilter);

    setContentLoading(true);
    setContentError(null);
    fetch(`/api/cms/content?${params.toString()}`)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to load content");
        const items = Array.isArray(data?.items)
          ? data.items.map((item: any) => ({
              ...item,
              slug_en: item.slug_en_draft ?? null,
              slug_fa: item.slug_fa_draft ?? null,
              slug_en_published: item.slug_en_published ?? null,
              slug_fa_published: item.slug_fa_published ?? null,
              title_en: item.title_en_draft ?? null,
              title_fa: item.title_fa_draft ?? null,
              title_en_published: item.title_en_published ?? null,
              title_fa_published: item.title_fa_published ?? null,
            }))
          : [];
        if (!cancelled) setContentItems(items);
      })
      .catch((err: any) => {
        if (!cancelled)
          setContentError(err?.message || "Failed to load content");
      })
      .finally(() => {
        if (!cancelled) setContentLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, debouncedSearch, tagFilter]);

  React.useEffect(() => {
    if (!open) return;
    setSearch(initialSlug ?? "");
    setTagFilter("");
    setSelectedCategoryId(null);
    setCollapsedNodes({});
  }, [open, initialSlug]);

  const filteredItems = React.useMemo(() => {
    return contentItems.filter((item) => {
      const slug =
        item.slug_en_published ||
        item.slug_fa_published ||
        item.slug_en ||
        item.slug_fa ||
        item.id;
      if (!slug) return false;
      if (!selectedCategoryId) return true;
      const categories =
        item.contentCategories
          ?.map((cc) => cc.contentCategory?.id)
          .filter(Boolean) ?? [];
      return categories.includes(selectedCategoryId);
    });
  }, [contentItems, selectedCategoryId]);

  const renderTree = (nodes: ContentCategoryNode[]) =>
    nodes.map((node) => {
      const hasChildren = !!node.children?.length;
      const collapsed = collapsedNodes[node.id];
      const isSelected = selectedCategoryId === node.id;
      const label = node.name_en || node.name_fa || "Untitled";

      return (
        <div key={node.id} className="space-y-1">
          <div className="flex items-center gap-1 rounded px-2 py-1 text-sm">
            <button
              type="button"
              aria-label={collapsed ? "Expand" : "Collapse"}
              className="text-gray-500"
              onClick={() => {
                if (!hasChildren) return;
                setCollapsedNodes((prev) => ({
                  ...prev,
                  [node.id]: !prev[node.id],
                }));
              }}
            >
              {hasChildren ? (
                collapsed ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )
              ) : (
                <span className="inline-block w-4" />
              )}
            </button>
            <button
              type="button"
              className={[
                "flex-1 truncate rounded px-2 py-1 text-left text-sm transition",
                isSelected
                  ? "bg-pinkCustom/10 text-pinkCustom"
                  : "hover:bg-pinkCustom/5",
              ].join(" ")}
              onClick={() => setSelectedCategoryId(node.id)}
            >
              {label}
            </button>
          </div>
          {hasChildren && !collapsed ? (
            <div className="space-y-1 border-l border-gray-100 pl-4">
              {renderTree(node.children!)}
            </div>
          ) : null}
        </div>
      );
    });

  const modalClassName = isSmall
    ? "!inset-0 !m-0 !w-full !rounded-none h-[100vh]"
    : "";
  const bodyClassName = isSmall ? "p-0 h-full flex flex-col" : "p-0";

  return (
    <CustomModal
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      maxWidth="max-w-5xl"
      bodyClassName={bodyClassName}
      className={modalClassName}
      top={isSmall ? "top-0" : "top-[5vh]"}
      closeOnOutsideClick
    >
      <div className={["flex flex-col", isSmall ? "h-full" : ""].join(" ")}>
        <div className="flex items-start justify-between gap-2 border-b border-gray-200 p-4">
          <div className="space-y-1">
            <div className="text-lg font-semibold text-gray-900">
              Insert content link
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-grayCustom text-slateCustom transition hover:text-pinkCustom"
            onClick={onClose}
          >
            <X className="h-4 w-4" strokeWidth={4} />
          </button>
        </div>

        <div className="flex flex-col gap-3 border-b border-gray-200 p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute top-2.5 left-2 h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search content..."
                className="focus:border-pinkCustom w-full rounded border border-gray-200 py-2 pr-2 pl-8 text-sm focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <select
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                className="focus:border-pinkCustom rounded border border-gray-200 bg-white px-2 py-2 text-sm focus:outline-none"
              >
                <option value="">All tags</option>
                {tags.map((tag) => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name_en || tag.name_fa || tag.id}
                  </option>
                ))}
              </select>
              {search || tagFilter || selectedCategoryId ? (
                <button
                  type="button"
                  className="text-xs text-gray-600 underline underline-offset-2"
                  onClick={() => {
                    setSearch("");
                    setTagFilter("");
                    setSelectedCategoryId(null);
                    setCollapsedNodes({});
                  }}
                >
                  Clear filters
                </button>
              ) : null}
            </div>
          </div>
          {tagsLoading || treeLoading || contentLoading ? (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading content data...
            </div>
          ) : null}
          {tagsError || treeError || contentError ? (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {tagsError || treeError || contentError}
            </div>
          ) : null}
        </div>

        <div className="flex-1 overflow-hidden">
          <div className="grid h-full grid-cols-1 divide-y divide-gray-100 sm:grid-cols-10 sm:divide-x sm:divide-y-0">
            <div className="overflow-auto p-4 sm:col-span-4">
              <div className="mb-2 text-xs font-semibold text-gray-500 uppercase">
                Content tree
              </div>
              {treeLoading ? (
                <div className="text-sm text-gray-500">Loading tree...</div>
              ) : categoryTree.length ? (
                <div className="space-y-1">{renderTree(categoryTree)}</div>
              ) : (
                <div className="text-sm text-gray-500">
                  No categories found.
                </div>
              )}
            </div>
            <div className="overflow-auto p-4 sm:col-span-6">
              <div className="mb-2 text-xs font-semibold text-gray-500 uppercase">
                {selectedCategoryId ? "Content in category" : "All content"}
              </div>
              {contentLoading && !filteredItems.length ? (
                <div className="text-sm text-gray-500">Loading content...</div>
              ) : !filteredItems.length ? (
                <div className="text-sm text-gray-500">
                  No content matches your filters.
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredItems.map((item) => {
                    const slug =
                      item.slug_en_published ||
                      item.slug_fa_published ||
                      item.slug_en ||
                      item.slug_fa ||
                      item.id;
                    const title =
                      item.title_en ||
                      item.title_fa ||
                      item.title_en_published ||
                      item.title_fa_published ||
                      slug;
                    return (
                      <button
                        key={`${item.id}-${slug}`}
                        type="button"
                        className="hover:border-pinkCustom/60 hover:bg-pinkCustom/5 w-full rounded border border-gray-200 bg-white px-3 py-2 text-left transition"
                        onClick={() => {
                          if (slug)
                            onSelect({ contentSlug: slug, contentId: item.id });
                        }}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-gray-900">
                              {title}
                            </div>
                            <div className="truncate text-xs text-gray-500">
                              {slug}
                            </div>
                          </div>
                          <span className="bg-pinkCustom/10 text-pinkCustom rounded-full px-2 py-[2px] text-[11px] font-semibold">
                            Select
                          </span>
                        </div>
                        {item.tags?.length ? (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {item.tags.map((tag) => (
                              <span
                                key={tag.id}
                                className="rounded-full bg-gray-100 px-2 py-[2px] text-[11px] text-gray-600"
                              >
                                {tag.name_en || tag.name_fa || tag.id}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </CustomModal>
  );
}

